import React, { useState } from 'react';
import { Code2, Copy, Check, ExternalLink, Globe, Sparkles } from 'lucide-react';
import { US_LOCATIONS } from '../services/locationData';

export const EmbedWidgetPage: React.FC = () => {
  const [selectedState, setSelectedState] = useState('ALL');
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [copied, setCopied] = useState(false);

  const embedUrl = selectedState === 'ALL'
    ? 'https://signalnear.pages.dev/'
    : `https://signalnear.pages.dev/location/${selectedState}`;

  const iframeSnippet = `<iframe
  src="${embedUrl}"
  width="100%"
  height="600"
  frameborder="0"
  style="border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 4px 12px rgba(0,0,0,0.05);"
  title="SignalNear Live Disruption Radar"
  allow="geolocation"
  loading="lazy"
></iframe>
<p style="font-size: 11px; color: #64748b; font-family: sans-serif; text-align: right; margin-top: 4px;">
  Live Alerts via <a href="https://signalnear.pages.dev" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: none;">SignalNear</a>
</p>`;

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(iframeSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-mono font-bold border border-blue-200">
          <Code2 className="w-3.5 h-3.5" />
          <span>FREE PUBLIC EMBED WIDGET</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-navy-950 mt-2">
          Embed Live Disruption Radar on Your Website
        </h1>
        <p className="text-sm sm:text-base text-slate-600 mt-1 leading-relaxed">
          Provide your local school, municipality, business, or community blog with a free, self-updating live radar widget powered by official National Weather Service bulletins.
        </p>
      </div>

      {/* Widget Configuration Card */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-6 shadow-xs space-y-4">
        <h2 className="text-base font-bold text-navy-950">
          Customize Your Embed
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="embed-state" className="text-xs font-bold text-slate-700 block mb-1">
              Location Scope:
            </label>
            <select
              id="embed-state"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-sans"
            >
              <option value="ALL">Nationwide US Disruption Radar</option>
              {US_LOCATIONS.map((loc) => (
                <option key={loc.code} value={loc.code}>
                  {loc.name} ({loc.code})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Generated Code Snippet */}
        <div className="space-y-2 pt-2">
          <div className="flex items-center justify-between text-xs font-mono text-slate-500">
            <span>COPY &amp; PASTE INTO YOUR HTML:</span>
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-1 px-3 py-1 bg-navy-950 text-white font-bold rounded-lg text-xs hover:bg-navy-900 transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? 'Copied to Clipboard!' : 'Copy Snippet'}</span>
            </button>
          </div>

          <pre className="p-4 bg-slate-950 text-slate-200 rounded-xl text-xs font-mono leading-relaxed overflow-x-auto border border-slate-800 selection:bg-cyan-500 selection:text-navy-950">
            {iframeSnippet}
          </pre>
        </div>
      </div>

      {/* Live Preview of Embed */}
      <div className="space-y-3">
        <h2 className="text-base font-bold text-navy-950">
          Live Interactive Preview
        </h2>
        <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-md">
          <iframe
            src={embedUrl}
            width="100%"
            height="500"
            className="w-full"
            title="SignalNear Live Disruption Radar Preview"
          />
        </div>
      </div>
    </div>
  );
};

